import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    ContentChild,
    Directive,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    HostListener,
    PLATFORM_ID,
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FreeDraggingHandleDirective } from './free-dragging-handle.directive';

@Directive({
    selector: '[appFreeDragging]',
})
export class FreeDraggingDirective implements AfterViewInit, OnDestroy {
    private element: HTMLElement;

    private subscriptions: Subscription[] = [];

    @ContentChild(FreeDraggingHandleDirective)
    handle: FreeDraggingHandleDirective;

    handleElement: HTMLElement;

    private readonly DEFAULT_DRAGGING_BOUNDARY_QUERY = 'body';
    @Input() boundaryQuery = this.DEFAULT_DRAGGING_BOUNDARY_QUERY;
    @Input() initialY = 0;
    draggingBoundaryElement: HTMLElement | HTMLBodyElement;
    isInitialed = false;

    @HostListener('window:resize', ['$event'])
    onResize(event?) {
        this.initializePosition();
    }

    constructor(
        @Inject(DOCUMENT) private document: any,
        @Inject(PLATFORM_ID) private platformId: object,
        private elementRef: ElementRef,
    ) {}

    ngAfterViewInit(): void {
        this.draggingBoundaryElement = (this.document as Document).querySelector(this.boundaryQuery);
        if (!this.draggingBoundaryElement) {
            throw new Error(`Couldn't find any element with query: ${this.boundaryQuery}`);
        } else {
            this.element = this.elementRef.nativeElement as HTMLElement;
            this.handleElement = this.handle?.elementRef?.nativeElement || this.element;
            this.initDrag();
        }
    }

    initializePosition() {
        if (isPlatformBrowser(this.platformId)) {
            if (window.innerWidth > 767) {
                this.element.style.transform = 'translate3d(0px, 0px, 0px)';
                this.isInitialed = false;
            } else if (!this.isInitialed) {
                this.element.style.transform = 'translate3d(0px, ' + this.initialY + 'px, 0)';
                this.isInitialed = true;
            }
        }
    }

    initDrag(): void {
        const dragStart$ = fromEvent<MouseEvent>(this.handleElement, 'mousedown');
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');
        const drag$ = fromEvent<MouseEvent>(this.document, 'mousemove').pipe(takeUntil(dragEnd$));

        let initialX: number;
        let initialY: number;
        let currentX = 0;
        let currentY = this.initialY;

        let dragSub: Subscription;

        const minBoundX = this.draggingBoundaryElement.offsetLeft;
        const minBoundY = this.draggingBoundaryElement.offsetTop;

        const maxBoundX = minBoundX + this.draggingBoundaryElement.offsetWidth - this.element.offsetWidth;
        const maxBoundY = minBoundY + this.draggingBoundaryElement.offsetHeight;
        // const maxBoundY = minBoundY + this.draggingBoundaryElement.offsetHeight - this.element.offsetHeight;

        const dragStartSub = dragStart$.subscribe((event: MouseEvent) => {
            initialX = event.clientX - currentX;
            initialY = event.clientY - currentY;
            this.isInitialed = true;

            dragSub = drag$.subscribe((eventDrag: MouseEvent) => {
                eventDrag.preventDefault();
                eventDrag.stopPropagation();

                const x = eventDrag.clientX - initialX;
                const y = eventDrag.clientY - initialY;

                currentX = Math.max(minBoundX, Math.min(x, maxBoundX));
                currentY = Math.max(minBoundY, Math.min(y, maxBoundY));

                this.element.style.transform = 'translate3d(0px, ' + currentY + 'px, 0)';
                this.initialY = currentY;
            });
        });

        const dragEndSub = dragEnd$.subscribe(() => {
            initialX = currentX;
            initialY = currentY;
            if (dragSub) {
                dragSub.unsubscribe();
            }
        });

        this.subscriptions.push.apply(this.subscriptions, [dragStartSub, dragSub, dragEndSub]);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s?.unsubscribe());
    }
}
